import { AttachmentItem } from "../../../components/Common/AttachmentInput";
import { customToast } from "../../../utils/custom-toast/custom-toast";

export const handleSendMessageWithAttachments = async (
  attachments: AttachmentItem[],
  handleSendMessage: (
    attachments?: Array<{
      s3_key: string;
      file_name: string;
      content_type: string;
      size_in_bytes: number;
    }>
  ) => Promise<void>
) => {
  if (attachments.length === 0) {
    await handleSendMessage();
    return;
  }

  // Get successfully uploaded attachments
  const uploadedAttachments = attachments
    .filter((att) => att.status === "sent" && att.remoteUrl)
    .map((att) => ({
      s3_key: att.remoteUrl!,
      file_name: att.name,
      content_type: att.mime || "",
      size_in_bytes: att.size || 0,
    }));

  // Check if there are any attachments still uploading
  const uploadingAttachments = attachments.filter(
    (att) => att.status === "uploading"
  );

  if (uploadingAttachments.length > 0) {
    customToast(
      "Please wait for all attachments to finish uploading",
      "warning"
    );
    return;
  }

  // Check if there are any failed attachments
  const failedAttachments = attachments.filter(
    (att) => att.status === "failed"
  );

  if (failedAttachments.length > 0) {
    customToast("Please remove failed attachments before sending", "error");
    return;
  }

  await handleSendMessage(
    uploadedAttachments.length > 0 ? uploadedAttachments : undefined
  );

  // Note: Attachments are now cleared by the onSuccess callback passed to handleSendMessage
  // This ensures they're only cleared after modal confirmation and successful send
};

export const createHandleAttachmentsChange = (
  isUpdatingRef: React.MutableRefObject<boolean>,
  attachmentsRef: React.MutableRefObject<AttachmentItem[]>,
  setAttachments: React.Dispatch<React.SetStateAction<AttachmentItem[]>>,
  uploadEmailAttachment: (payload: {
    attachment: File;
    source: string;
  }) => Promise<unknown>
) => {
  return async (newAttachments: AttachmentItem[]) => {
    // Prevent infinite loop - if we're already updating, don't process again
    if (isUpdatingRef.current) {
      return;
    }

    const currentAttachments = attachmentsRef.current;

    // Check if attachments actually changed by comparing IDs and counts
    const currentIds = new Set(currentAttachments.map((a) => a.id));
    const newIds = new Set(newAttachments.map((a) => a.id));
    const idsChanged =
      currentIds.size !== newIds.size ||
      !Array.from(newIds).every((id) => currentIds.has(id));

    // If IDs haven't changed, this might be a prop sync - check if it's just status updates
    if (!idsChanged) {
      // Check if it's just status/progress updates (from our own uploads)
      const hasStatusChanges = newAttachments.some((newItem) => {
        const currentItem = currentAttachments.find((c) => c.id === newItem.id);
        return (
          currentItem &&
          (currentItem.status !== newItem.status ||
            currentItem.progress !== newItem.progress ||
            currentItem.remoteUrl !== newItem.remoteUrl)
        );
      });

      // If it's just status updates from our uploads, update state but don't trigger new uploads
      if (hasStatusChanges) {
        isUpdatingRef.current = true;
        setAttachments(newAttachments);
        attachmentsRef.current = newAttachments;
        setTimeout(() => {
          isUpdatingRef.current = false;
        }, 0);
      }
      return;
    }

    // Find newly added attachments (those with file but no remoteUrl and not already uploading/sent)
    const newItems = newAttachments.filter(
      (item) =>
        item.file &&
        !item.remoteUrl &&
        item.status !== "uploading" &&
        item.status !== "sent" &&
        !currentAttachments.some((existing) => existing.id === item.id)
    );

    // Update state with new attachments (including removals)
    isUpdatingRef.current = true;
    setAttachments(newAttachments);
    attachmentsRef.current = newAttachments;

    // Use setTimeout to break the synchronous update cycle
    setTimeout(() => {
      isUpdatingRef.current = false;
    }, 0);

    // If no new items to upload, return early
    if (newItems.length === 0) return;

    // Mark new items as uploading
    setAttachments((prev) => {
      const updated = prev.map((item) =>
        newItems.some((newItem) => newItem.id === item.id)
          ? { ...item, status: "uploading" as const, progress: 0 }
          : item
      );
      attachmentsRef.current = updated;
      return updated;
    });

    // Upload each new attachment
    newItems.forEach(async (item) => {
      if (!item.file) return;

      try {
        const response = await uploadEmailAttachment({
          attachment: item.file,
          source: "conversation",
        });

        if (
          response &&
          typeof response === "object" &&
          "data" in response &&
          response.data
        ) {
          const apiResponse = Array.isArray(response.data)
            ? response.data[0]
            : response.data;

          if (apiResponse) {
            const updatedAttachment: AttachmentItem = {
              ...item,
              status: "sent" as const,
              progress: 100,
              remoteUrl: apiResponse.s3_key,
              s3Url: apiResponse.s3_url,
              name: apiResponse.file_name || item.name,
              size: apiResponse.size_in_bytes || item.size,
              mime: apiResponse.content_type || item.mime,
            };

            setAttachments((prev) => {
              const updated = prev.map((att) =>
                att.id === item.id ? updatedAttachment : att
              );
              attachmentsRef.current = updated;
              return updated;
            });
          } else {
            throw new Error("No response data");
          }
        }
      } catch (error) {
        console.error(`Error uploading attachment: ${item.name}`, error);
        setAttachments((prev) => {
          const updated = prev.map((att) =>
            att.id === item.id
              ? {
                  ...att,
                  status: "failed" as const,
                  progress: 0,
                }
              : att
          );
          attachmentsRef.current = updated;
          return updated;
        });
        customToast(
          `Failed to upload ${item.name}. Please try again.`,
          "error"
        );
      }
    });
  };
};
